import {compact} from '@benchling/js-utils';
import React from 'react';

import {getLabelColumns} from 'bulk-import/getLabelColumns';
import getRegistryEntityRequiredColumnsError from 'bulk-import/getRegistryEntityRequiredColumnsError';
import * as StorableColumnsUtil from 'bulk-import/storables/StorableColumnsUtil';
import {NavStoreSource} from 'constants/SourceConstants';
import {UploadFlowType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {ComplexPolymerSchemaConfigGraphQL} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {canSchemaHaveMods} from 'molbio/OligoUtil';
import {TagSchemaSelectorQuery} from 'schemas/shared/__generated/TagSchemaSelectorQuery';
import {TAG_SCHEMA_SELECTOR_QUERY} from 'schemas/shared/TagSchemaOrInterfaceSelector';
import * as ModalContainer from 'ui-library/ModalContainer';
import {withLoadingScreen} from 'util/Promises';

import ImportFilesTutorialLink from './ImportFilesTutorialLink';
import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  DERIVED_COLUMNS_SUPPORTED_XFORM_CONFIGS,
  DerivedColumnsStoreKeys,
  IMPORT_TYPE,
  OPTIONS,
  Pane,
  PaneKey,
  SelectedSchemaIds,
  SPREADSHEET_BULK_IMPORT_KEY,
  STORABLE_COLUMN_TYPES,
  TaskState,
  TRANSFER_MEASUREMENT_TYPES,
  VALUE_COLUMN_TYPES,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';
import * as SpreadsheetBulkImportUtil from './SpreadsheetBulkImportUtil';

export interface BulkCreateRegistryEntitiesFlowProps {
  initialRegistry: NavStoreSource | null;
  onRequestClose: () => void;
  complexPolymerSchemaConfigurations?: Array<ComplexPolymerSchemaConfigGraphQL>;
}

class BulkCreateRegistryEntitiesFlow extends React.Component<BulkCreateRegistryEntitiesFlowProps> {
  static async open(props: Omit<BulkCreateRegistryEntitiesFlowProps, 'onRequestClose'>): Promise<void> {
    await withLoadingScreen(doQuerySafe<TagSchemaSelectorQuery>({query: TAG_SCHEMA_SELECTOR_QUERY}));
    ModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkCreateRegistryEntitiesFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
    );
  }

  _getPanes: () => Array<Pane> = () =>
    compact([
      {
        paneKey: PaneKey.SET_IMPORT_SETTINGS_FOR_REGISTRY,
        configuration: null,
      },
      {
        paneKey: PaneKey.UPLOAD_SPREADSHEET,
        configuration: UploadSpreadsheetModalPane.configure({
          getExampleImageURL({selectedSchemaIds}: {selectedSchemaIds: SelectedSchemaIds}): string {
            // Show screenshots with 'Bases' if importing sequences ('Amino Acids', proteins).
            const tagSchema = SpreadsheetBulkImportUtil.getTagSchema(selectedSchemaIds);
            return SpreadsheetBulkImportUtil.getExampleImageURLForTagSchema(tagSchema);
          },
        }),
      },
      {
        paneKey: PaneKey.MAP_COLUMN_NAMES,
        configuration: configureMapColumnNamesModalPane({
          getValidColumnTypes: ({registryImportSettings, options, selectedSchemaIds}) => {
            const labelColumns = getLabelColumns(registryImportSettings);
            const containerColumnTypes = options[OPTIONS.IMPORT_CONTAINERS]
              ? [
                  ...STORABLE_COLUMN_TYPES,
                  COLUMN_TYPE.NUMBER_OF_CONTAINERS,
                  COLUMN_TYPE.CONTAINER_SCHEMA_FIELD,
                  ...TRANSFER_MEASUREMENT_TYPES,
                  ...StorableColumnsUtil.getSampleControlRolesColumns(),
                ]
              : [];
            return compact([
              ...labelColumns,
              ...SpreadsheetBulkImportUtil.getPolymerColumnTypes(
                SpreadsheetBulkImportUtil.shouldHaveResidues(selectedSchemaIds),
                canSchemaHaveMods(SpreadsheetBulkImportUtil.getTagSchema(selectedSchemaIds)!)
              ),
              ...SpreadsheetBulkImportUtil.getMoleculeColumnTypes(
                SpreadsheetBulkImportUtil.shouldHaveChemicalStructure(selectedSchemaIds)
              ),
              ...SpreadsheetBulkImportUtil.getComplexPolymerColumnTypes(
                selectedSchemaIds,
                this.props.complexPolymerSchemaConfigurations
              ),
              ...VALUE_COLUMN_TYPES,
              ...containerColumnTypes,
            ]);
          },
          getRequiredColumnsError: getRegistryEntityRequiredColumnsError,
          onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForCreateRegistryEntities,
          customHandleGoToNextPaneFn: (paneProps) => {
            if (SpreadsheetBulkImportUtil.shouldCheckDerivedColumns(paneProps.fileData.columns)) {
              SpreadsheetBulkImportActions.findDerivedColumns();
              SpreadsheetBulkImportActions.goToNextPane();
            } else {
              paneProps.onValidateSpreadsheet();
              SpreadsheetBulkImportActions.goToPaneWithKey(PaneKey.SHOW_ERRORS);
            }
          },
          savedMappingsConfig: {
            enableSavedMappings: true,
            uploadFlow: UploadFlowType.BULK_CREATE_AND_REGISTER_ENTITIES,
          },
        }),
      },
      {
        paneKey: PaneKey.MAP_DERIVED_COLUMNS,
        configuration: {
          columnTransformConfigs: DERIVED_COLUMNS_SUPPORTED_XFORM_CONFIGS,
          derivedColumnsKey: DerivedColumnsStoreKeys.BULK_CREATE,
          onUpdateDerivedColumns: SpreadsheetBulkImportActions.setDerivedColumns,
          onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForCreateRegistryEntities,
        },
      },
      {
        paneKey: PaneKey.SHOW_ERRORS,
        configuration: ShowErrorsModalPane.configure({
          customHandleGoToPrevPaneFn: (paneProps) => {
            if (
              paneProps.derivedColumnsData.state === TaskState.COMPLETED &&
              paneProps.derivedColumnsData.derivedColumns &&
              paneProps.derivedColumnsData.derivedColumns.length > 0
            ) {
              SpreadsheetBulkImportActions.goToPrevPane();
            } else {
              SpreadsheetBulkImportActions.goToPaneWithKey(PaneKey.MAP_COLUMN_NAMES);
            }
          },
          enableReplaceLinkWithText: true,
          enableSkipErrors: true,
          uploadFlow: UploadFlowType.BULK_CREATE_AND_REGISTER_ENTITIES,
        }),
      },
    ]);

  render(): JSX.Element {
    return (
      <SpreadsheetBulkImportModalContainer
        footer={<ImportFilesTutorialLink />}
        importType={IMPORT_TYPE.REGISTRY_ENTITIES}
        initialSettings={{initialRegistry: this.props.initialRegistry}}
        onImportFromSpreadsheet={
          SpreadsheetBulkImportActions.createRegistryEntitiesFromSpreadsheetWithDataMods
        }
        onRequestClose={this.props.onRequestClose}
        panes={this._getPanes()}
        title="Import entities from spreadsheet"
        complexPolymerSchemaConfigurations={this.props.complexPolymerSchemaConfigurations}
      />
    );
  }
}

export default BulkCreateRegistryEntitiesFlow;
